import React, { RefObject, useEffect, useRef, useState } from 'react';
import { Box, Checkbox, InputLabel } from '@hopskipdrive/react-components';
import { Close, KeyboardArrowDown } from '@mui/icons-material';
import { includes, uniqueId } from 'lodash';

import { MultiSelectContainer } from './MultiSelectDropdown.styles';

interface IMultiSelectProps {
  options: Option[];
  handleChange: (ids: string[]) => void;
  types: string[];
}

interface Option {
  value: string;
  label: string;
}

export const MultiSelect: React.FC<IMultiSelectProps> = ({ options = [], handleChange, types }) => {
  const wrapperRef = useRef(null) as RefObject<HTMLDivElement>;
  const [showOptions, setShowOptions] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, false);

    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, []);

  useEffect(() => {
    setSelectedKeys(types);
  }, [types]);

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setShowOptions(false);
    }
  };

  const handleFilterChange = (value: string, event) => {
    if (includes(selectedKeys, value)) {
      removeFilter(value);
    } else {
      setSelectedKeys([...selectedKeys, value]);
      handleChange([...selectedKeys, value]);
    }

    event?.stopPropagation();
  };

  const removeFilter = (value: string) => {
    const newFilterState = selectedKeys.filter((filter) => {
      return filter !== value;
    });

    setSelectedKeys(newFilterState);
    handleChange(newFilterState);
  };

  return (
    <MultiSelectContainer ref={wrapperRef}>
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key == ' ') {
            setShowOptions(true);
          }
        }}
        className="multi-dropdown"
        data-testid="alert-type-filter"
        onClick={() => setShowOptions(true)}
      >
        <span>{selectedKeys.length ? 'Alert Types' : 'All Alert Types'}</span>
        <KeyboardArrowDown />
      </div>

      {showOptions && (
        <div className="dropdown-content">
          <span className="nib"></span>
          <ul>
            {options.map((option) => {
              return (
                <li className="option" key={uniqueId()}>
                  <Box display="flex" alignItems="center">
                    <Checkbox
                      id={option.value}
                      checked={includes(selectedKeys, option.value)}
                      onClick={(event) => {
                        handleFilterChange(option.value, event);
                      }}
                    />
                    <InputLabel className="option-label" htmlFor={option.value} data-testid={option.value}>
                      {option.label}
                    </InputLabel>
                  </Box>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <ul className="selected-filters">
        {selectedKeys.map((value) => (
          <li className="filter" key={uniqueId()}>
            <span>{options?.find((option) => option.value === value)?.label}</span>
            <Close
              stroke="red"
              width={16}
              onClick={() => {
                removeFilter(value);
              }}
            />
          </li>
        ))}
      </ul>
    </MultiSelectContainer>
  );
};
